import React from 'react';
import { TicketsTabTrip } from 'Services/Api/_HumanWritten/BookingWizardDataService';

interface Params {
	/** The sailing the booking currently holds for this leg, by id. */
	ticketId: string | undefined;
	/** That sailing, found in the window that is loaded. Undefined when it is not in it. */
	selectedTrip: TicketsTabTrip | undefined;
	/** The window itself. `null` before the first load, which is not the same as empty. */
	trips: TicketsTabTrip[] | null;
	/** Anything still in flight that could still produce the sailing. */
	loading: boolean;
	/** Drop the booking's hold on this leg, so the step offers the calendar again. */
	clear: () => void;
}

/**
 * Let go of a sailing the loaded window can no longer account for.
 *
 * <p>The step draws a spinner while it holds a ticket id it cannot find a sailing for, on the
 * assumption that the sailing is on its way. That is true while a fetch is running and false the
 * moment one finishes without it — and the case that finishes without it is the ordinary one:
 * change the route, or the direction, and the booking is still holding a sailing that belongs to
 * the route it used to be on. Nothing was ever going to arrive, so the spinner ran forever.</p>
 *
 * <p>The three guards are all load-bearing. `loading` covers a fetch in flight, including the
 * alteration flow's background window fetch, which deliberately renders a single trip first.
 * `trips == null` is "has not loaded yet", which an empty array is not. And the ticket is cleared
 * only when it is genuinely absent from a window that has arrived.</p>
 */
export function useClearStaleTicket({ ticketId, selectedTrip, trips, loading, clear }: Params) {
	React.useEffect(() => {
		if (loading || !ticketId || selectedTrip || trips === null) {
			return;
		}
		clear();
		// `clear` closes over the wizard data it is rebuilding, so it is a new function on every
		// render; keying the effect on it would run this on every render instead of on the one
		// thing that changed.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loading, ticketId, selectedTrip, trips]);
}
